<div class="p-6 flex flex-col gap-10">
  <section class="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-slate-800">Certificates &amp; Badges</h2>
      <div class="dropdown dropdown-end">
        <button tabindex="0" class="btn btn-sm bg-sky-500 hover:bg-sky-600 text-white border-none">Create New</button>
        <ul tabindex="0" class="dropdown-content z-[1] menu bg-base-100 rounded-box border border-slate-100 w-40 p-2 shadow">
          <li><a (click)="openModal('certificate')">Certificate</a></li>
          <li><a (click)="openModal('badge')">Badges</a></li>
        </ul>
      </div>
    </div>

    <div class="overflow-x-auto">
      <table class="table w-full">
        <thead>
          <tr class="text-slate-500">
            <th class="font-medium">Title</th>
            <th class="font-medium">Course</th>
            <th class="font-medium">Type</th>
            <th class="font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngIf="loadingRecords">
            <td colspan="5" class="text-center text-slate-500 py-6">Loading...</td>
          </tr>
          <tr *ngIf="!loadingRecords && records.length === 0">
            <td colspan="5" class="text-center text-slate-500 py-6">No records found</td>
          </tr>
          <tr *ngFor="let record of paginatedRecords">
            <td>{{ record.title }}</td>
            <td>{{ record.course_name }}</td>
            <td>{{ record.typeLabel }}</td>
            <td class="text-right">
              <div class="flex items-center justify-end gap-2">
                <button class="btn btn-xs btn-ghost border border-slate-200" (click)="openEdit(record)">
                  <i class="ri-edit-line"></i>
                </button>
                <button class="btn btn-xs btn-ghost border border-slate-200" (click)="deleteRecord(record)">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4 flex items-center justify-between" *ngIf="!loadingRecords && records.length > 0">
      <p class="text-sm text-slate-500">
        Showing {{ (currentPage - 1) * pageSize + 1 }} -
        {{ ((currentPage - 1) * pageSize + paginatedRecords.length) }} of {{ records.length }}
      </p>
      <app-pagination [data]="paginationData" (pageChange)="goToPage($event)"></app-pagination>
    </div>

  </section>

  <!-- ═══════════════════════════════════════════
       CERTIFICATE MODAL
  ═══════════════════════════════════════════ -->
  <div *ngIf="activeModal === 'certificate'" class="fixed inset-0 z-2 flex items-center justify-center">
    <div class="absolute inset-0 bg-slate-900/40" (click)="closeModal()"></div>
    <div class="relative bg-white w-[95%] max-w-3xl rounded-xl shadow-xl p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-slate-800">
          {{ certificateEditId ? 'Edit Certificate' : 'Create New Certificate' }}
        </h2>
        <div class="flex items-center gap-3">
          <button class="btn btn-sm bg-sky-500 hover:bg-sky-600 text-white border-none" (click)="saveCertificate()" [disabled]="saving || loadingEdit">
            {{ loadingEdit ? 'Loading...' : (saving ? 'Saving...' : 'Save') }}
          </button>
          <button class="btn btn-sm bg-slate-400 hover:bg-slate-500 text-white border-none" (click)="closeModal()">Cancel</button>
        </div>
      </div>

      <p *ngIf="loadingEdit" class="mb-4 text-sm text-slate-500">Loading certificate details...</p>

      <form [formGroup]="certificateFormGroup" class="grid grid-cols-1 md:grid-cols-2 gap-6">

        <!-- Course Name -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Course Name</label>
          <select class="select select-bordered w-full" formControlName="course_id">
            <option value="">
              {{ courseLoading ? 'Loading courses...' : 'Select course' }}
            </option>
            <option *ngFor="let course of courseOptions" [ngValue]="course.id">
              {{ course.course_name }}
            </option>
          </select>
          <span class="text-xs text-red-500" *ngIf="getError(certificateFormGroup, 'course_id', certificateSubmitted) as err">
            {{ err }}
          </span>
        </div>

        <!-- Certificate Title -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Certificate Title</label>
          <input
            type="text"
            class="input input-bordered w-full"
            formControlName="title"
            [attr.minlength]="MIN_TITLE"
            [attr.maxlength]="MAX_TITLE"
          />
          <span class="text-xs text-red-500" *ngIf="getError(certificateFormGroup, 'title', certificateSubmitted) as err">
            {{ err }}
          </span>
        </div>

        <!-- Type of Certificate -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Type of Certificate</label>
          <select class="select select-bordered w-full" formControlName="certificate_type">
            <option value="">Select type</option>
            <option>Course Completion</option>
            <option>Partial Completion</option>
            <option>Test Completion</option>
          </select>
          <span class="text-xs text-red-500" *ngIf="getError(certificateFormGroup, 'certificate_type', certificateSubmitted) as err">
            {{ err }}
          </span>
        </div>

        <!-- Date -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Date</label>
          <select class="select select-bordered w-full" formControlName="date_type">
            <option value="">Select date type</option>
            <option>Specific Date (Fixed date)</option>
            <option>Date of Issue (On achievement)</option>
          </select>
          <span class="text-xs text-red-500" *ngIf="getError(certificateFormGroup, 'date_type', certificateSubmitted) as err">
            {{ err }}
          </span>
        </div>

        <!-- Signature -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Signature</label>
          <label class="border border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center gap-2 text-slate-500 cursor-pointer">
            <i class="ri-upload-cloud-2-line text-xl"></i>
            <span class="text-sm">{{ certificateFormGroup.get('signature')?.value?.name || 'Upload' }}</span>
            <img
              *ngIf="certificateFormGroup.get('signature')?.value?.name && (
                certificateFormGroup.get('signature')?.value?.name?.includes('.png') ||
                certificateFormGroup.get('signature')?.value?.name?.includes('.jpg') ||
                certificateFormGroup.get('signature')?.value?.name?.includes('.jpeg') ||
                certificateFormGroup.get('signature')?.value?.name?.includes('.webp')
              )"
              [src]="certificateFormGroup.get('signature')?.value?.name"
              class="w-20 h-20 object-cover rounded border border-slate-200"
              alt="certificate signature"
              onerror="this.style.display='none'"
            />
            <input type="file" class="hidden" (change)="onSignatureSelected($event)" />
          </label>
          <span class="text-xs text-red-500" *ngIf="getError(certificateFormGroup, 'signature', certificateSubmitted) as err">
            {{ err }}
          </span>

          <span class="text-xs text-slate-900">(Whose signature is needed on the Certificate.)</span>
        </div>

      </form>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════
       BADGE MODAL
  ═══════════════════════════════════════════ -->
  <div *ngIf="activeModal === 'badge'" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-slate-900/40" (click)="closeModal()"></div>
    <div class="relative bg-white w-[95%] max-w-3xl rounded-xl shadow-xl p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-slate-800">
          {{ badgeEditId ? 'Edit Badge' : 'Create New Badge' }}
        </h2>
        <div class="flex items-center gap-3">
          <button class="btn btn-sm bg-sky-500 hover:bg-sky-600 text-white border-none" (click)="saveBadge()" [disabled]="saving || loadingEdit">
            {{ loadingEdit ? 'Loading...' : (saving ? 'Saving...' : 'Save') }}
          </button>
          <button class="btn btn-sm bg-slate-400 hover:bg-slate-500 text-white border-none" (click)="closeModal()">Cancel</button>
        </div>
      </div>

      <p *ngIf="loadingEdit" class="mb-4 text-sm text-slate-500">Loading badge details...</p>

      <form [formGroup]="badgeFormGroup" class="grid grid-cols-1 md:grid-cols-2 gap-6">

        <!-- Course Name -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Course Name</label>
          <select class="select select-bordered w-full" formControlName="course_id">
            <option value="">
              {{ courseLoading ? 'Loading courses...' : 'Select course' }}
            </option>
            <option *ngFor="let course of courseOptions" [ngValue]="course.id">
              {{ course.course_name }}
            </option>
          </select>
          <span class="text-xs text-red-500" *ngIf="getError(badgeFormGroup, 'course_id', badgeSubmitted) as err">
            {{ err }}
          </span>
        </div>

        <!-- Badge Title -->
        <div class="flex flex-col gap-2">
          <label class="text-sm text-slate-600">Badge Title</label>
          <input
            type="text"
            class="input input-bordered w-full"
            formControlName="title"
            [attr.minlength]="MIN_TITLE"
            [attr.maxlength]="MAX_TITLE"
          />
          <span class="text-xs text-red-500" *ngIf="getError(badgeFormGroup, 'title', badgeSubmitted) as err">
            {{ err }}
          </span>
        </div>

        <!-- Description -->
        <div class="flex flex-col gap-2 md:col-span-2">
          <label class="text-sm text-slate-600">Description</label>
          <input
            type="text"
            class="input input-bordered w-full"
            formControlName="description"
            [attr.minlength]="MIN_DESCRIPTION"
            [attr.maxlength]="MAX_DESCRIPTION"
          />
          <span class="text-xs text-red-500" *ngIf="getError(badgeFormGroup, 'description', badgeSubmitted) as err">
            {{ err }}
          </span>
        </div>

      </form>
    </div>
  </div>

</div>